// React imports
import React, { useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import countryCodes from 'country-codes-list';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare, faSquareCheck, faShip, faPlane, faTruck, faCalendarDays, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { faCircle as faCircleSolid } from '@fortawesome/pro-solid-svg-icons';

// Mui imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import { alpha, useTheme } from '@mui/material/styles';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { green } from '@mui/material/colors';

// Local imports
import OpportunitiesFilterOwner from './OpportunitiesFilter.owner.js';
import readableId from '../../ReadableId';
import PIPELINE from '../accounts/AccountFunnelPipeline.js';
import ZPopper from './ZPopper.js';

import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);

function OpportunitiesFilter(props) {
    const { filter, setFilter, org, me, opportunities, accounts, groupedDays, hideTaskType, hideRoutes, hideTaskStatus, hideSearch, hidePipeline, sortBy, setSortBy } = props;

    const originOptions = useMemo(() =>
        _.sortBy(_.uniq((opportunities || []).flatMap(o => [...(o.routes || []), ...((o.offers || []).flatMap(off => off.routes || []))].map(r => r.source?.text)).filter(Boolean))),
    [opportunities]);

    const destinationOptions = useMemo(() =>
        _.sortBy(_.uniq((opportunities || []).flatMap(o => [...(o.routes || []), ...((o.offers || []).flatMap(off => off.routes || []))].map(r => r.destination?.text)).filter(Boolean))),
    [opportunities]);

    const searchOptions = useMemo(() => {
        const tags = _([...opportunities, ...(accounts || [])])
            .map(a => a.tags)
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .filter(a => !(filter.tags || []).find(t => _.isEqual(t,a)))
            .uniqBy(a => a?.label)
            .value();
        const _accounts = _(accounts)
            .filter(a => a?.name)
            .map(a => a.name)
            .sort()
            .filter(a => !(filter.accounts || []).find(t => t?.label?.toUpperCase() === a?.toUpperCase()))
            .uniq()
            .value();
        return [
            ...tags.map(t => ({ type: 'tag', value: t })), 
            ..._accounts.map(n => ({ type: 'name', value: n })),
        ]
    }, [accounts, filter]);

    const theme = useTheme();

    // Compute active date range
    const dateRange = useMemo(() => {
        if (!groupedDays || !filter.days || filter.days === 'all') return null;
        const from = (() => {
            if (filter.days === 'week') return dayjs().startOf('isoWeek');
            if (filter.days === 'month') return dayjs().startOf('month');
            if (filter.days === 'quarter') return dayjs().startOf('quarter');
            if (filter.days === 'past-week') return dayjs().subtract(1, 'week').startOf('isoWeek');
            if (filter.days === 'past-month') return dayjs().subtract(1, 'month').startOf('month');
            if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').startOf('quarter');
            if (filter.days === 'custom' && filter.dateFrom) return dayjs(filter.dateFrom);
            return null;
        })();
        const to = (() => {
            if (filter.days === 'past-week') return dayjs().subtract(1, 'week').endOf('isoWeek');
            if (filter.days === 'past-month') return dayjs().subtract(1, 'month').endOf('month');
            if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').endOf('quarter');
            if (filter.days === 'custom' && filter.dateTo) return dayjs(filter.dateTo);
            return dayjs();
        })();
        if (!from) return null;
        return { from, to };
    }, [filter.days, filter.dateFrom, filter.dateTo, groupedDays]);

    // Shared pill styles
    const pillSx = {
        fontSize: '11px', fontWeight: 500, textTransform: 'none',
        px: '10px', py: '3px', minWidth: 0, borderRadius: '14px',
        lineHeight: 1.5, whiteSpace: 'nowrap',
    };
    const pillActive = {
        ...pillSx,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: 'primary.main', fontWeight: 600,
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
    };
    const pillInactive = {
        ...pillSx,
        color: 'text.secondary',
        '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.04) },
    };

    // Transport toggle helper
    const transportBtn = (mode, icon) => {
        const active = (filter.transportationMethods || []).includes(mode);
        return (
            <Tooltip title={_.capitalize(mode)} key={mode}>
                <IconButton
                    size='small'
                    onClick={() => setFilter(f => ({
                        ...f,
                        transportationMethods: active
                            ? (f.transportationMethods || []).filter(m => m !== mode)
                            : [...(f.transportationMethods || []), mode]
                    }))}
                    sx={{
                        width: 32, height: 32, borderRadius: '8px',
                        color: active ? 'primary.main' : 'text.disabled',
                        backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        border: `1px solid ${active ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.text.primary, 0.08)}`,
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                    }}
                >
                    <FontAwesomeIcon icon={icon} style={{ fontSize: '13px' }} />
                </IconButton>
            </Tooltip>
        );
    };

    // Opportunity status pills config
    const STATUS_PILLS = [
        { label: 'Draft', filter: { taskStatus: 'open', openTasksFilter: ['New'] } },
        { label: 'Sent', filter: { taskStatus: 'open', openTasksFilter: ['Sent to Customer'] } },
        { label: 'Opened', filter: { taskStatus: 'open', openTasksFilter: ['Opened by Customer'] } },
        { label: 'Accepted', filter: { taskStatus: 'open', openTasksFilter: ['Accepted by Customer'] } },
        { label: 'Rejected', filter: { taskStatus: 'open', openTasksFilter: ['Rejected by Customer'] } },
        { label: 'Closed', filter: { taskStatus: 'closed', openTasksFilter: [] }, color: '#475569' },
        { label: 'Won', filter: { taskStatus: 'won', openTasksFilter: [] }, color: green[600] },
        { label: 'Lost', filter: { taskStatus: 'lost', openTasksFilter: [] }, color: theme.palette.error.main },
    ];

    const isStatusPillActive = (pill) => {
        if (pill.filter.taskStatus === 'closed') return filter.taskStatus === 'closed';
        if (pill.filter.taskStatus === 'won') return filter.taskStatus === 'won';
        if (pill.filter.taskStatus === 'lost') return filter.taskStatus === 'lost';
        return filter.taskStatus === 'open' && (filter.openTasksFilter || []).includes(pill.filter.openTasksFilter[0]);
    };

    return (
        <Box sx={{
            backgroundColor: '#F9FAFB',
            border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
            borderRadius: '12px',
            p: '12px 16px',
            mb: 2,
        }}>
            {/* ─── TIER 1: Primary Filters ─── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>

                {/* Owner (was in tier 2, moved here) */}
                <OpportunitiesFilterOwner org={org} filter={filter} setFilter={setFilter} me={me} noMarginRight={true} />

                {/* Date capsule */}
                {groupedDays && (
                    <>
                        {!hideSearch && <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />}
                        <Select
                            size='small'
                            value={filter.days}
                            onChange={(e) => setFilter({ ...filter, days: e.target.value })}
                            renderValue={(val) => {
                                const labels = { week: 'This Week', month: 'This Month', quarter: 'This Quarter', 'past-week': 'Last Week', 'past-month': 'Last Month', 'past-quarter': 'Last Quarter', custom: 'Custom', all: 'All Time' };
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: '12px', opacity: 0.5 }} />
                                        <span>{labels[val] || val}</span>
                                        {dateRange && <Typography component='span' sx={{ fontSize: '10px', opacity: 0.6, ml: '2px' }}>
                                            ({dateRange.from.format('MMM D')} – {dateRange.to.format('MMM D')})
                                        </Typography>}
                                    </Box>
                                );
                            }}
                            sx={{
                                fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff',
                                '& .MuiSelect-select': { py: '5px', pr: '28px !important' },
                                minWidth: 0,
                            }}
                            MenuProps={{ style: { zIndex: 1000000 } }}
                        >
                            <MenuItem value='week'>This Week</MenuItem>
                            <MenuItem value='month'>This Month</MenuItem>
                            <MenuItem value='quarter'>This Quarter</MenuItem>
                            <MenuItem value='past-week'>Last Week</MenuItem>
                            <MenuItem value='past-month'>Last Month</MenuItem>
                            <MenuItem value='past-quarter'>Last Quarter</MenuItem>
                            <MenuItem value='custom'>Custom Range</MenuItem>
                            <MenuItem value='all'>All Time</MenuItem>
                        </Select>
                    </>
                )}
                {!groupedDays && (
                    <Select size='small' sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', '& .MuiSelect-select': { py: '5px' } }} value={filter.days} onChange={(e) => setFilter({ ...filter, days: e.target.value })}>
                        <MenuItem value={2}>Last 2 days</MenuItem>
                        <MenuItem value={7}>Last 7 days</MenuItem>
                        <MenuItem value={30}>Last 30 days</MenuItem>
                        <MenuItem value={90}>Last 90 days</MenuItem>
                        <MenuItem value='custom'>Custom</MenuItem>
                        <MenuItem value={5000}>All Time</MenuItem>
                    </Select>
                )}

                {/* Custom date pickers */}
                {filter.days === 'custom' && (
                    <>
                        <MobileDatePicker
                            label='From'
                            inputFormat='DD/MM/YYYY'
                            value={filter.dateFrom}
                            onChange={(v) => setFilter({ ...filter, dateFrom: v })}
                            renderInput={(params) => <TextField size='small' sx={{ width: 140, '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }} {...params} />}
                        />
                        <MobileDatePicker
                            label='To'
                            inputFormat='DD/MM/YYYY'
                            value={filter.dateTo}
                            onChange={(v) => setFilter({ ...filter, dateTo: v })}
                            renderInput={(params) => <TextField size='small' sx={{ width: 140, '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }} {...params} />}
                        />
                    </>
                )}

                {!hideSearch && (
                    <>
                        <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                        {/* Transport mode toggles */}
                        <Box sx={{ display: 'flex', gap: '4px' }}>
                            {transportBtn('sea', faShip)}
                            {transportBtn('air', faPlane)}
                            {transportBtn('land', faTruck)}
                        </Box>

                        {!hideTaskStatus && (
                            <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />
                        )}

                        {/* Opportunity status pills */}
                        {!hideTaskStatus && <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', alignSelf: 'center', mr: '-2px' }}>Status</Typography>}
                        {!hideTaskStatus && <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                            {/* All tasks pill */}
                            <Button
                                size='small'
                                onClick={() => setFilter(f => ({ ...f, taskStatus: 'all', openTasksFilter: [] }))}
                                sx={filter.taskStatus === 'all' ? pillActive : pillInactive}
                            >
                                All
                            </Button>
                            {STATUS_PILLS.map((pill) => {
                                const active = isStatusPillActive(pill);
                                const color = pill.color || theme.palette.primary.main;
                                return (
                                    <Button
                                        key={pill.label}
                                        size='small'
                                        onClick={() => {
                                            if (active) {
                                                setFilter(f => ({ ...f, taskStatus: 'all', openTasksFilter: [] }));
                                            } else {
                                                setFilter(f => ({ ...f, ...pill.filter }));
                                            }
                                        }}
                                        sx={active ? {
                                            ...pillSx,
                                            backgroundColor: alpha(color, 0.1),
                                            color: color, fontWeight: 600,
                                            '&:hover': { backgroundColor: alpha(color, 0.18) },
                                        } : pillInactive}
                                        startIcon={active ? <FontAwesomeIcon icon={faCircleSolid} style={{ fontSize: '6px', color }} /> : null}
                                    >
                                        {pill.label}
                                    </Button>
                                );
                            })}
                        </Box>}

                        {!hideTaskStatus && (
                            <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />
                        )}

                        {/* Pipeline stage filter */}
                        {!hideTaskStatus && (
                            <>
                                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', alignSelf: 'center', mr: '-2px' }}>Pipeline</Typography>
                                <Select
                                    size='small'
                                    displayEmpty
                                    value={filter.pipelineStage || 'all'}
                                    onChange={(e) => setFilter(f => ({ ...f, pipelineStage: e.target.value === 'all' ? null : e.target.value }))}
                                    sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', minWidth: 120, '& .MuiSelect-select': { py: '5px' } }}
                                    MenuProps={{ style: { zIndex: 1000000 } }}
                                >
                                    <MenuItem value='all'>All Stages</MenuItem>
                                    <MenuItem value='New Inquiry'>New Inquiry</MenuItem>
                                    <MenuItem value='Cost Research'>Cost Research</MenuItem>
                                    <MenuItem value='Ready for Sales'>Ready for Sales</MenuItem>
                                    <MenuItem value='Pending Customer'>Pending Customer</MenuItem>
                                    <MenuItem value='Follow-Up'>Follow-Up</MenuItem>
                                    <MenuItem value='Closed'>Closed</MenuItem>
                                </Select>
                            </>
                        )}

                    </>
                )}
            </Box>

            {/* ─── Date range display ─── */}
            {dateRange && (
                <Typography variant='caption' sx={{ display: 'block', mt: '6px', ml: '2px', color: 'text.secondary', fontSize: '11px' }}>
                    Showing data from <b>{dateRange.from.format('ddd, MMM D')}</b> to <b>{dateRange.to.format('ddd, MMM D')}</b>
                </Typography>
            )}

            {/* ─── Origin / Destination filters ─── */}
            {!hideSearch && (
                <Box sx={{ display: 'flex', gap: 2, mt: '8px', flexWrap: 'wrap' }}>
                    <Autocomplete
                        multiple freeSolo size='small'
                        options={originOptions}
                        value={filter.originFilter || []}
                        onChange={(e, v) => setFilter(f => ({ ...f, originFilter: v }))}
                        filterOptions={(options, { inputValue }) => inputValue ? options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())) : options}
                        renderInput={(params) => <TextField {...params} label='Origin' placeholder='Type to search...' />}
                        sx={{ flex: 1, minWidth: '180px', '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }}
                    />
                    <Autocomplete
                        multiple freeSolo size='small'
                        options={destinationOptions}
                        value={filter.destinationFilter || []}
                        onChange={(e, v) => setFilter(f => ({ ...f, destinationFilter: v }))}
                        filterOptions={(options, { inputValue }) => inputValue ? options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())) : options}
                        renderInput={(params) => <TextField {...params} label='Destination' placeholder='Type to search...' />}
                        sx={{ flex: 1, minWidth: '180px', '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }}
                    />
                </Box>
            )}

            {/* ─── TIER 2: Search + Secondary Refinements ─── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '8px', flexWrap: 'wrap' }}>
                {!hideSearch && (
                    <Autocomplete
                        size='small'
                        sx={{ flex: 1, minWidth: 200 }}
                        options={searchOptions}
                        filterOptions={(options, state) => {
                            const { inputValue } = state;
                            const type = inputValue.startsWith('#') ? ['tag'] : inputValue.startsWith('@') ? ['name'] : null;
                            if (!type) return [];
                            const value = inputValue.substr(1);
                            if (value.length === 0) return [];
                            return options.filter(o => type.includes(o.type) && (o.value.label || o.value).toLowerCase().startsWith(value.toLowerCase()));
                        }}
                        getOptionLabel={option => {
                            if (typeof option === 'string') return option;
                            if (typeof option.value === 'string') return option.value;
                            if (typeof option.value?.label === 'string') return option.value.label;
                            return '';
                        }}
                        renderOption={(props, option) => {
                            const label = typeof option.value === 'string' ? option.value : option.value?.label || '';
                            const typeLabel = option.type === 'tag' ? '#' : option.type === 'name' ? '@' : option.type === 'transportationMethod' ? '🚛' : '';
                            const typeColor = option.type === 'tag' ? '#8b5cf6' : option.type === 'name' ? '#3b82f6' : '#64748b';
                            const typeName = option.type === 'tag' ? 'Tag' : option.type === 'name' ? 'Account' : option.type === 'transportationMethod' ? 'Mode' : '';
                            return (
                                <li {...props}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: typeColor, minWidth: '16px' }}>{typeLabel}</Typography>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{label}</Typography>
                                        <Typography sx={{ fontSize: '10px', color: '#94a3b8', ml: 'auto' }}>{typeName}</Typography>
                                    </Box>
                                </li>
                            );
                        }}
                        freeSolo
                        multiple
                        onChange={(e, value) => {
                            const tags = [
                                ...value.filter(v => v.type === 'tag').map(t => t.value),
                                ...value.filter(v => typeof (v) === 'string' && v.startsWith('#')).map(t => ({ label: t.substr(1) }))
                            ];
                            const _accounts = [
                                ...value.filter(v => v.type === 'name').map(t => t.value),
                                ...value.filter(v => typeof (v) === 'string' && v.startsWith('@')).map(t => t.substr(1))
                            ].map(ac => ac).map(ac => ({
                                label: ac,
                                ids: (accounts || []).filter(a => a && (a?.name || '').toUpperCase() === (ac || '').toUpperCase()).map(a => a?.id).filter(id => id !== undefined && id !== null)
                            }));
                            const transportationMethods = value.filter(v => v.type === 'transportationMethod').map(t => t.value);
                            const freetext = [
                                ...value.filter(v => v.type === 'freetext').map(t => t.value),
                                ...value.filter(v => typeof (v) === 'string' && !v.startsWith('#') && !v.startsWith('@')).map(t => t)
                            ];
                            setFilter(f => ({ ...f, tags, accounts: _accounts, freetext, transportationMethods }));
                        }}
                        renderTags={(value, props) => _.compact([
                            ...(filter.tags || []).map((t, i) => <Chip size='small' label={t.label} {...props({ index: i })} />),
                            ...(filter.accounts || []).map((n, i) => <Chip size='small' label={n.label} {...props({ index: (filter.tags || []).length + i })} />),
                            ...(filter.transportationMethods || []).map((n, i) => <Chip size='small' label={_.capitalize(n)} {...props({ index: (filter.tags || []).length + (filter.accounts || []).length + i })} />),
                            ...(filter.freetext || []).map((n, i) => <Chip size='small' label={n} {...props({ index: (filter.tags || []).length + (filter.accounts || []).length + (filter.transportationMethods || []).length + i })} />),
                        ])}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder='Search...'
                                size='small'
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <InputAdornment position='start'>
                                                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: '12px', opacity: 0.4 }} />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                }}
                                sx={{ '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }}
                            />
                        )}
                        value={[
                            ...(filter.tags || []).map(t => ({ type: 'tag', value: t })),
                            ...(filter.accounts || []).map(n => ({ type: 'name', value: n.label })),
                            ...(filter.transportationMethods || []).map(t => ({ type: 'transportationMethod', value: t })),
                            ...(filter.freetext || []).map(n => ({ type: 'freetext', value: n }))
                        ]}
                        slots={{ popper: ZPopper }}
                        PopperComponent={ZPopper}
                    />
                )}

                {!hideTaskType && (
                    <Select
                        size='small'
                        displayEmpty
                        value={filter.taskType}
                        onChange={(e) => setFilter({ ...filter, taskType: e.target.value, openTasksFilter: [] })}
                        sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', minWidth: 100, '& .MuiSelect-select': { py: '5px' } }}
                        MenuProps={{ style: { zIndex: 1000000 } }}
                    >
                        <MenuItem value='all'>All Types</MenuItem>
                        <MenuItem value='one-off'>One-offs</MenuItem>
                        <MenuItem value='contract'>Contracts</MenuItem>
                    </Select>
                )}
                {!hidePipeline && (
                    <Select
                        size='small'
                        displayEmpty
                        value={filter.pipeline || 'all'}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === 'all') {
                                setFilter(f => ({ ...f, pipeline: null, tags: (f.tags || []).filter(t => !t.label?.startsWith('Pipeline: ')) }));
                            } else {
                                setFilter(f => ({ ...f, pipeline: val, tags: [...(f.tags || []).filter(t => !t.label?.startsWith('Pipeline: ')), { label: `Pipeline: ${val}` }] }));
                            }
                        }}
                        sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', minWidth: 120, '& .MuiSelect-select': { py: '5px' } }}
                        MenuProps={{ style: { zIndex: 1000000 } }}
                    >
                        <MenuItem value='all'>All Accounts</MenuItem>
                        {PIPELINE.map(stage => (
                            <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                        ))}
                    </Select>
                )}
                {org.address && !hideRoutes && (
                    <Select
                        size='small'
                        value={filter.routes || 'all'}
                        onChange={e => setFilter({ ...filter, routes: e.target.value === 'all' ? null : e.target.value })}
                        sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', minWidth: 120, '& .MuiSelect-select': { py: '5px' } }}
                        MenuProps={{ style: { zIndex: 1000000 } }}
                    >
                        <MenuItem value='all'>All Routes</MenuItem>
                        <MenuItem value='import'>Import</MenuItem>
                        <MenuItem value='export'>Export</MenuItem>
                    </Select>
                )}
            </Box>
        </Box>
    );
}

function applyFilter({ filter, opportunities = [], org, accounts }) {
    const __t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const HOUR = 1 * 60 * 60 * 1000;
    const DAY = 24 * HOUR;

    const dateFrom = (() => {
        if (filter.days === 'week') return dayjs().startOf('isoWeek').toDate();
        if (filter.days === 'month') return dayjs().startOf('month').toDate();
        if (filter.days === 'quarter') return dayjs().startOf('quarter').toDate();
        if (filter.days === 'past-week') return dayjs().subtract(1, 'week').startOf('isoWeek').toDate();
        if (filter.days === 'past-month') return dayjs().subtract(1, 'month').startOf('month').toDate();
        if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').startOf('quarter').toDate();
        return new Date(filter.days === 'custom' ? (filter.dateFrom ? filter.dateFrom : new Date().getTime()) : new Date().getTime() - filter.days * DAY);
    })();
    dateFrom.setHours(0); dateFrom.setMinutes(0); dateFrom.setSeconds(0); dateFrom.setMilliseconds(0);
    const dateTo = (() => {
        if (filter.days === 'past-week') return dayjs().subtract(1, 'week').endOf('isoWeek').toDate();
        if (filter.days === 'past-month') return dayjs().subtract(1, 'month').endOf('month').toDate();
        if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').endOf('quarter').toDate();
        return new Date(filter.days === 'custom' && filter.dateTo ? filter.dateTo : new Date().getTime());
    })();
    dateTo.setHours(23); dateTo.setMinutes(59); dateTo.setSeconds(59); dateTo.setMilliseconds(999);
    const dateDays = Math.floor((dateTo.getTime() - dateFrom.getTime()) / 1000 / 60 / 60 / 24);

    // --- Precompute all per-filter constants outside the loop ---
    const dateFromMs = dateFrom.getTime();
    const dateToMs = dateTo.getTime();
    const tmSet = !_.isEmpty(filter.transportationMethods) ? new Set(filter.transportationMethods) : null;
    const membersSet = (filter.members && filter.members.length > 0) ? new Set(filter.members) : null;
    const accountIdsSet = !_.isEmpty(filter.accounts) ? new Set(_.flatten(filter.accounts.map(a => a.ids))) : null;
    const filterTags = filter.tags || [];
    const hasTagFilter = filterTags.length > 0;
    const freetextList = (filter.freetext || []).map(f => f.toLowerCase());
    const hasFreetextFilter = freetextList.length > 0;
    const hasOpenTasksFilter = filter.taskStatus === 'open' && filter.openTasksFilter?.length > 0;
    const pipelineStageFilter = filter.pipelineStage || null;
    const closedStatuses = new Set(['completed', 'lost', 'endcustomer-accepted', 'endcustomer-rejected']);

    // Accounts by id — linear lookup becomes O(1)
    const accountsById = new Map();
    for (const a of (accounts || [])) if (a && a.id) accountsById.set(a.id, a);

    // --- Two-pass filter: cheap rejections first, expensive predicates last ---
    // Pass 1: cheap numeric/set/string checks to quickly drop the bulk of opportunities
    const pass1 = [];
    for (const opportunity of opportunities) {
        if (opportunity.id === org.id) continue;
        // Date (numeric, day-granularity via dateFromMs/dateToMs which are already midnight/end-of-day)
        const created = opportunity.created;
        if (created < dateFromMs || created > dateToMs) continue;
        if (tmSet && !tmSet.has(opportunity.transportationMethod)) continue;
        if (membersSet && !membersSet.has(opportunity.owner)) continue;
        if (accountIdsSet && (!opportunity.account || !accountIdsSet.has(opportunity.account.id))) continue;

        // Pipeline stage
        if (pipelineStageFilter) {
            if (pipelineStageFilter === 'Closed') {
                if (!closedStatuses.has(opportunity.status)) continue;
            } else {
                if (closedStatuses.has(opportunity.status)) continue;
                const pTag = (opportunity.tags || []).find(t => t.label?.startsWith('Pipeline: '));
                const stage = pTag ? pTag.label.replace('Pipeline: ', '') : 'New Inquiry';
                if (stage !== pipelineStageFilter) continue;
            }
        }

        // taskType
        if (filter.taskType !== 'all') {
            const opportunityType = opportunity.type === 'one-off-email' || opportunity.type === 'one-off-free-text' ? 'one-off' : opportunity.type;
            if (opportunityType !== filter.taskType) continue;
        }
        // taskStatus (cheap: string compare, before the expensive openTasksFilter branch)
        if (filter.taskStatus === 'closed') {
            if (opportunity.status !== 'completed' && opportunity.status !== 'lost') continue;
        } else if (filter.taskStatus === 'lost') {
            if (opportunity.status !== 'lost') continue;
        } else if (filter.taskStatus === 'won') {
            if (opportunity.status !== 'completed') continue;
        } else if (filter.taskStatus === 'open') {
            if (opportunity.status === 'lost' || opportunity.status === 'completed') continue;
        }

        pass1.push(opportunity);
    }

    // Pass 2: expensive predicates (tag intersection with account tags, openTasks log parsing,
    // routes flatten, freetext JSON.stringify) run only on the already-narrowed subset.
    const pass2 = [];
    for (const opportunity of pass1) {
        // Tags (only if there's a tag filter — needs to merge account tags)
        if (hasTagFilter) {
            const acct = opportunity.account?.id ? accountsById.get(opportunity.account.id) : null;
            const oppTags = opportunity.tags || [];
            const acctTags = (acct && acct.tags) || [];
            // intersectionWith with _.isEqual is the semantic; on a narrowed subset the cost is tolerable
            if (_.isEmpty(_.intersectionWith(filterTags, [...oppTags, ...acctTags], _.isEqual))) continue;
        }

        // openTasksFilter log parsing (only when status is 'open' and filter is set)
        if (hasOpenTasksFilter) {
            let log = _.last(_(opportunity).get('quotes[0].log', []).filter(l => l.action === 'modified' && JSON.parse(l.params || '{}').status !== 'completed'));
            if (!log) log = { timestamp: opportunity.modified || opportunity.created, params: JSON.stringify({ status: opportunity.status === 'completed' ? null : opportunity.status }) };
            const pass = !log.params
                ? filter.openTasksFilter.includes('New')
                : !opportunity.status
                    ? filter.openTasksFilter.includes('New')
                    : filter.openTasksFilter.includes(statusToText({ opportunity }));
            if (!pass) continue;
        }

        // Freetext (JSON.stringify is expensive; only touch opportunities that passed everything else)
        if (hasFreetextFilter) {
            const oppJson = JSON.stringify(opportunity).toLowerCase();
            const acct = opportunity.account?.id ? accountsById.get(opportunity.account.id) : null;
            const acctJson = acct ? JSON.stringify(acct).toLowerCase() : '';
            const readable = readableId(opportunity).toLowerCase();
            let anyMiss = false;
            for (const q of freetextList) {
                if (oppJson.indexOf(q) === -1 && acctJson.indexOf(q) === -1 && readable !== q) {
                    anyMiss = true;
                    break;
                }
            }
            if (anyMiss) continue;
        }

        // Origin/Destination
        if ((filter.originFilter || []).length > 0) {
            const routes = [...(opportunity.routes || []), ...(opportunity.offers?.flatMap(o => o.routes || []) || [])];
            const origins = routes.map(r => r.source?.text || '');
            if (!filter.originFilter.some(f => origins.some(o => o.toLowerCase().includes(f.toLowerCase())))) continue;
        }
        if ((filter.destinationFilter || []).length > 0) {
            const routes = [...(opportunity.routes || []), ...(opportunity.offers?.flatMap(o => o.routes || []) || [])];
            const destinations = routes.map(r => r.destination?.text || '');
            if (!filter.destinationFilter.some(f => destinations.some(d => d.toLowerCase().includes(f.toLowerCase())))) continue;
        }

        // Routes
        if (filter.routes) {
            const routes = [...(opportunity.routes || []), ...(opportunity.offers?.map(o => o.routes || []).flat() || [])];
            if (filter.routes === 'import') {
                if (!routes.find(r => r.destination?.countryCode === org.address?.countryCode)) continue;
            } else if (filter.routes === 'export') {
                if (!routes.find(r => r.source?.countryCode === org.address?.countryCode)) continue;
            }
        }

        pass2.push(opportunity);
    }

    const sorted = _.sortBy(pass2, opportunity => -1 * opportunity.created);
    const __t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const __dt = __t1 - __t0;
    // Only warn when it's meaningful (>15ms) to avoid spamming
    if (__dt > 15) {
        console.log(`[perf] applyFilter: ${__dt.toFixed(1)}ms  in=${opportunities.length}  pass1=${pass1.length}  out=${sorted.length}  freetext=${JSON.stringify(filter.freetext || [])}  days=${filter.days}  taskType=${filter.taskType}  taskStatus=${filter.taskStatus}`);
    }
    return sorted;
}

export default OpportunitiesFilter;
export { applyFilter };

const STATUS_TO_TEXT = {
    'new': 'New',
    'sent-to-endcustomer': 'Sent to Customer',
    'opened-by-customer': 'Opened by Customer',
    'endcustomer-accepted': 'Accepted by Customer',
    'endcustomer-rejected': 'Rejected by Customer',
    'lost': 'Lost',
    'completed': 'Won',
    'open': 'Open',
};

function statusToText({ opportunity }) {
    return STATUS_TO_TEXT[opportunity.status || 'new'];
}

function countryCodeToCountry(code) {
    return (countryCodes.findOne('countryCode', code) || {}).countryNameEn || code;
}